/**
 * `useContactNames` — an address → contact-name index for the EMAIL tab.
 *
 * The operator's rule for the list column is "show a name; show the address
 * only when no name is known", and the header's own display-name is not enough:
 * `mail.list` routinely returns a bare address for `to`, so a sent-folder row
 * would otherwise read as a long machine address. The contacts store is the
 * authoritative source for what a correspondent is CALLED.
 *
 * Deliberately forgiving: contacts are a NICETY here, never a dependency. If
 * `contacts.list` is missing on an older runtime, or fails, or the gateway is
 * still connecting, the hook returns an empty index and every caller degrades
 * to header display-names and then to raw addresses. It must never be able to
 * stop mail from rendering.
 */
import { useEffect, useMemo, useState } from "react";

import { useGatewayStore } from "@/stores/useGatewayStore";
import { buildContactNameIndex } from "@/lib/mail-address";
import type { Contact } from "@/types/contacts";

export function useContactNames(): {
  /** Lowercased address → curated display name. Empty when unavailable. */
  index: Map<string, string>;
  /** Convenience lookup with the same fallback semantics. */
  resolve: (address: string) => string | undefined;
} {
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const [contacts, setContacts] = useState<Contact[]>([]);

  useEffect(() => {
    if (!rpc || connectionState !== "connected") return;
    let cancelled = false;
    // Archived contacts are excluded: an archived correspondent should not keep
    // supplying a name to new mail.
    rpc
      .contactsList({})
      .then((res) => {
        if (!cancelled) setContacts(res.contacts ?? []);
      })
      .catch(() => {
        // Method-not-found on an older runtime, or any transport failure —
        // names are optional, so swallow and fall back to the headers.
        if (!cancelled) setContacts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [rpc, connectionState]);

  const index = useMemo(() => buildContactNameIndex(contacts), [contacts]);
  const resolve = useMemo(() => (address: string) => index.get(address), [index]);
  return { index, resolve };
}
